import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';

import { feedbackApi } from '../../services/api';
import { formatApiErrorDetail } from '../../utils/apiError';
import {
  FeedbackMenuTargetOption,
  FeedbackModuleId,
  FeedbackPayload,
  FeedbackTargetsResponse,
  FEEDBACK_COMMENT_MAX_LENGTH,
} from '../../types';
import StarRating from './StarRating';
import './FeedbackForm.css';

export interface FeedbackFormProps {
  initialWorkflowId?: number | null;
  initialModule?: FeedbackModuleId | null;
  initialMenuKey?: string | null;
  allowGeneralFeedback?: boolean;
  allowWorkflowChange?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  compact?: boolean;
}

interface FormErrors {
  rating?: string;
  comment?: string;
  scope?: string;
  submit?: string;
}

type TargetSelection = '' | `menu:${string}` | `workflow:${number}`;

const sanitizeComment = (raw: string): string => {
  return raw.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u200B-\u200D\uFEFF]/g, '');
};

const GROUP_LABELS: Record<string, string> = {
  reports: 'Relatórios',
  settings: 'Configurações',
};

interface MenuGroupBlock {
  groupKey: string | null;
  menus: FeedbackMenuTargetOption[];
}

const FeedbackForm: React.FC<FeedbackFormProps> = memo(({
  initialWorkflowId = null,
  initialModule = null,
  initialMenuKey = null,
  allowGeneralFeedback = true,
  allowWorkflowChange = true,
  onSuccess,
  onCancel,
  compact = false,
}) => {
  const [targets, setTargets] = useState<FeedbackTargetsResponse | null>(null);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [selectedModule, setSelectedModule] = useState<FeedbackModuleId | ''>('');
  const [selectedTarget, setSelectedTarget] = useState<TargetSelection>('');
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const isMountedRef = useRef<boolean>(true);
  const submitAbortRef = useRef<AbortController | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    const controller = new AbortController();
    setLoadingTargets(true);

    feedbackApi
      .getTargets(controller.signal, initialWorkflowId ?? undefined)
      .then((data) => {
        if (!isMountedRef.current) return;
        setTargets(data);
      })
      .catch((err: unknown) => {
        if (!isMountedRef.current) return;
        if (err instanceof Error && err.name === 'CanceledError') return;
        setErrors((prev) => ({
          ...prev,
          scope: 'Não foi possível carregar as opções de avaliação.',
        }));
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        setLoadingTargets(false);
      });

    return () => {
      isMountedRef.current = false;
      controller.abort();
      submitAbortRef.current?.abort();
    };
  }, [initialWorkflowId]);

  const workflowPrefill = targets?.prefill_workflow ?? null;
  const isWorkflowContext =
    !allowWorkflowChange && initialWorkflowId != null;

  useEffect(() => {
    if (!targets || initializedRef.current) return;
    initializedRef.current = true;

    if (initialWorkflowId != null && targets.prefill_workflow?.id === initialWorkflowId) {
      const prefill = targets.prefill_workflow;
      setSelectedModule(prefill.module);
      setSelectedTarget(`workflow:${prefill.id}`);
      return;
    }

    if (initialModule) {
      setSelectedModule(initialModule);
      if (initialMenuKey) {
        setSelectedTarget(`menu:${initialMenuKey}`);
      }
      return;
    }

    if (allowGeneralFeedback) {
      setSelectedModule('platform');
    }
  }, [targets, initialWorkflowId, initialModule, initialMenuKey, allowGeneralFeedback]);

  const moduleOptions = useMemo(() => {
    if (!targets) return [];
    return targets.modules.filter(
      (m) => allowGeneralFeedback || m.id !== 'platform',
    );
  }, [targets, allowGeneralFeedback]);

  const currentModuleTargets = useMemo(() => {
    if (!selectedModule || selectedModule === 'platform' || !targets) {
      return null;
    }
    return targets.targets_by_module[selectedModule] ?? { menus: [], workflows: [] };
  }, [selectedModule, targets]);

  const menuGroups = useMemo((): MenuGroupBlock[] => {
    const menus = currentModuleTargets?.menus ?? [];
    const blocks: MenuGroupBlock[] = [];
    const ungrouped: FeedbackMenuTargetOption[] = [];
    const byGroup: Record<string, FeedbackMenuTargetOption[]> = {};

    for (const menu of menus) {
      if (menu.group) {
        if (!byGroup[menu.group]) {
          byGroup[menu.group] = [];
        }
        byGroup[menu.group].push(menu);
      } else {
        ungrouped.push(menu);
      }
    }

    if (ungrouped.length > 0) {
      blocks.push({ groupKey: null, menus: ungrouped });
    }
    for (const groupKey of Object.keys(byGroup)) {
      blocks.push({ groupKey, menus: byGroup[groupKey] });
    }
    return blocks;
  }, [currentModuleTargets]);

  const isScopeDisabled = useMemo(
    () =>
      isWorkflowContext ||
      (!allowWorkflowChange && initialModule != null),
    [allowWorkflowChange, initialModule, isWorkflowContext],
  );

  const characterCount = comment.length;
  const isOverLimit = characterCount > FEEDBACK_COMMENT_MAX_LENGTH;

  const showSecondSelect =
    selectedModule !== '' && selectedModule !== 'platform' && !isWorkflowContext;

  const handleModuleChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as FeedbackModuleId | '';
    setSelectedModule(value);
    setSelectedTarget('');
    setErrors((prev) => ({ ...prev, scope: undefined }));
  }, []);

  const handleTargetChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTarget(event.target.value as TargetSelection);
    setErrors((prev) => ({ ...prev, scope: undefined }));
  }, []);

  const handleRatingChange = useCallback((value: number) => {
    setRating(value);
    setErrors((prev) => ({ ...prev, rating: undefined }));
  }, []);

  const handleCommentChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setComment(sanitizeComment(event.target.value));
      setErrors((prev) => ({ ...prev, comment: undefined }));
    },
    [],
  );

  const validate = useCallback((): boolean => {
    const next: FormErrors = {};
    if (rating < 1 || rating > 5) {
      next.rating = 'Selecione uma nota de 1 a 5.';
    }
    if (comment.length > FEEDBACK_COMMENT_MAX_LENGTH) {
      next.comment = `O comentário deve ter no máximo ${FEEDBACK_COMMENT_MAX_LENGTH} caracteres.`;
    }
    if (!selectedModule) {
      next.scope = 'Selecione sobre qual parte da plataforma você está avaliando.';
    } else if (selectedModule !== 'platform' && !selectedTarget) {
      next.scope = 'Selecione o menu que deseja avaliar.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [comment, rating, selectedModule, selectedTarget]);

  const buildPayload = useCallback((): FeedbackPayload => {
    const trimmed = comment.trim();
    const base: FeedbackPayload = {
      rating,
      is_anonymous: isAnonymous,
      comment: trimmed.length === 0 ? null : trimmed,
    };

    if (selectedModule === 'platform') {
      return {
        ...base,
        feedback_module: 'platform',
        feedback_subject_type: null,
        feedback_subject_key: null,
        workflow_id: null,
      };
    }

    if (selectedTarget.startsWith('menu:')) {
      return {
        ...base,
        feedback_module: selectedModule as FeedbackModuleId,
        feedback_subject_type: 'menu',
        feedback_subject_key: selectedTarget.slice(5),
        workflow_id: null,
      };
    }

    if (selectedTarget.startsWith('workflow:')) {
      return {
        ...base,
        feedback_module: selectedModule as FeedbackModuleId,
        feedback_subject_type: 'workflow',
        feedback_subject_key: null,
        workflow_id: Number(selectedTarget.slice(9)),
      };
    }

    return base;
  }, [comment, isAnonymous, rating, selectedModule, selectedTarget]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitting || !validate()) return;

      submitAbortRef.current?.abort();
      const controller = new AbortController();
      submitAbortRef.current = controller;

      setSubmitting(true);
      setErrors((prev) => ({ ...prev, submit: undefined }));

      try {
        await feedbackApi.create(buildPayload(), controller.signal);
        if (!isMountedRef.current) return;
        setSuccess(true);
        onSuccess?.();
      } catch (err: unknown) {
        if (!isMountedRef.current) return;
        if (err instanceof Error && err.name === 'CanceledError') return;
        const message = (() => {
          if (typeof err === 'object' && err !== null && 'response' in err) {
            const response = (err as { response?: { data?: { detail?: unknown } } }).response;
            const detail = response?.data?.detail;
            if (detail) {
              return formatApiErrorDetail(detail);
            }
          }
          return 'Não foi possível enviar seu feedback. Tente novamente.';
        })();
        setErrors((prev) => ({ ...prev, submit: message }));
      } finally {
        if (isMountedRef.current) setSubmitting(false);
      }
    },
    [buildPayload, onSuccess, submitting, validate],
  );

  const renderMenuOptions = () => {
    const elements: React.ReactNode[] = [];

    for (const block of menuGroups) {
      if (!block.groupKey) {
        for (const menu of block.menus) {
          elements.push(
            <option key={`menu-${menu.key}`} value={`menu:${menu.key}`}>
              {menu.label}
            </option>,
          );
        }
        continue;
      }

      const groupKey = block.groupKey;
      elements.push(
        <optgroup key={groupKey} label={GROUP_LABELS[groupKey] ?? groupKey}>
          {block.menus.map((menu: FeedbackMenuTargetOption) => (
            <option key={`menu-${groupKey}-${menu.key}`} value={`menu:${menu.key}`}>
              {menu.label}
            </option>
          ))}
        </optgroup>,
      );
    }

    return elements;
  };

  if (success) {
    return (
      <div className={`feedback-form feedback-form--success ${compact ? 'is-compact' : ''}`}>
        <div className="feedback-form__success">
          <i className="bi bi-check-circle-fill" aria-hidden="true" />
          <h5>Obrigado pelo seu feedback!</h5>
          <p className="text-muted mb-0">
            Suas respostas nos ajudam a evoluir a plataforma continuamente.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Form
      noValidate
      onSubmit={handleSubmit}
      className={`feedback-form ${compact ? 'is-compact' : ''}`}
    >
      <div className="feedback-form__rating">
        <Form.Label className="feedback-form__label">
          Como você avalia sua experiência? <span className="text-danger">*</span>
        </Form.Label>
        <StarRating
          value={rating}
          onChange={handleRatingChange}
          size={compact ? 'md' : 'lg'}
          ariaLabel="Avaliação de 1 a 5 estrelas"
        />
        {errors.rating && (
          <div className="invalid-feedback d-block">{errors.rating}</div>
        )}
      </div>

      {moduleOptions.length > 0 && (
        <Form.Group className="feedback-form__field" controlId="feedback-module">
          <Form.Label className="feedback-form__label">
            Sobre qual parte da plataforma?
          </Form.Label>
          <Form.Select
            value={selectedModule}
            onChange={handleModuleChange}
            disabled={isScopeDisabled || loadingTargets}
            aria-describedby="feedback-module-help"
          >
            <option value="">Selecione…</option>
            {moduleOptions.map((mod) => (
              <option key={mod.id} value={mod.id}>
                {mod.label}
              </option>
            ))}
          </Form.Select>
          <Form.Text id="feedback-module-help" className="text-muted">
            Escolha a plataforma como um todo ou um módulo específico.
          </Form.Text>
        </Form.Group>
      )}

      {isWorkflowContext && workflowPrefill && (
        <p className="feedback-form__context-hint text-muted small mb-0">
          Avaliando o fluxo <strong>{workflowPrefill.name}</strong> desta requisição.
        </p>
      )}

      {showSecondSelect && (
        <Form.Group className="feedback-form__field" controlId="feedback-target">
          <Form.Label className="feedback-form__label">
            O que você quer avaliar?
          </Form.Label>
          <Form.Select
            value={selectedTarget}
            onChange={handleTargetChange}
            disabled={isScopeDisabled || loadingTargets}
            aria-describedby="feedback-target-help"
          >
            <option value="">Selecione…</option>
            {(currentModuleTargets?.menus.length ?? 0) > 0 && renderMenuOptions()}
          </Form.Select>
          <Form.Text id="feedback-target-help" className="text-muted">
            Apenas telas e menus que seu perfil pode acessar neste módulo.
          </Form.Text>
          {errors.scope && (
            <div className="invalid-feedback d-block">{errors.scope}</div>
          )}
        </Form.Group>
      )}

      {errors.scope && !showSecondSelect && selectedModule === '' && (
        <div className="invalid-feedback d-block mb-2">{errors.scope}</div>
      )}

      <Form.Group className="feedback-form__field" controlId="feedback-comment">
        <Form.Label className="feedback-form__label">
          Comentário <span className="text-muted">(opcional)</span>
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={compact ? 3 : 4}
          value={comment}
          onChange={handleCommentChange}
          maxLength={FEEDBACK_COMMENT_MAX_LENGTH + 50}
          placeholder="Conte rapidamente o que motivou sua nota..."
          isInvalid={isOverLimit || Boolean(errors.comment)}
          aria-describedby="feedback-comment-counter"
        />
        <div className="feedback-form__counter" id="feedback-comment-counter">
          <span className={isOverLimit ? 'text-danger' : 'text-muted'}>
            {characterCount}/{FEEDBACK_COMMENT_MAX_LENGTH}
          </span>
        </div>
        {errors.comment && (
          <div className="invalid-feedback d-block">{errors.comment}</div>
        )}
      </Form.Group>

      <Form.Group className="feedback-form__field" controlId="feedback-anonymous">
        <Form.Check
          type="switch"
          checked={isAnonymous}
          onChange={(e) => setIsAnonymous(e.target.checked)}
          label={
            <span>
              Enviar como <strong>anônimo</strong>
              <span className="text-muted ms-1">(sua identidade ficará oculta)</span>
            </span>
          }
        />
      </Form.Group>

      {errors.submit && (
        <Alert variant="danger" className="feedback-form__alert">
          {errors.submit}
        </Alert>
      )}

      <div className="feedback-form__actions">
        {onCancel && (
          <Button type="button" variant="outline-secondary" onClick={onCancel} disabled={submitting}>
            Agora não
          </Button>
        )}
        <Button type="submit" variant="success" disabled={submitting || isOverLimit}>
          {submitting ? (
            <>
              <Spinner as="span" size="sm" animation="border" role="status" aria-hidden="true" />
              <span className="ms-2">Enviando...</span>
            </>
          ) : (
            <>
              <i className="bi bi-send-fill me-2" aria-hidden="true" />
              Enviar feedback
            </>
          )}
        </Button>
      </div>
    </Form>
  );
});

FeedbackForm.displayName = 'FeedbackForm';

export default FeedbackForm;

